import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { exportFaelligkeiten, exportRadsaetzeUebersicht } from '../utils/pdfExport';

function formatDate(d) {
  if (!d) return '–';
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y}`;
}

function addMonths(dateStr, months) {
  if (!dateStr || !months) return '';
  const d = new Date(dateStr);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

// ─── Badges ──────────────────────────────────────────────────────────────────

function DiffBadge({ diffDays, ueberfaellig }) {
  if (ueberfaellig) return <span className="badge badge-danger">Überfällig ({Math.abs(diffDays)}d)</span>;
  if (diffDays <= 7) return <span className="badge badge-danger">In {diffDays} Tag(en)</span>;
  return <span className="badge badge-warning">In {diffDays} Tagen</span>;
}

function TypBadge({ typ }) {
  return <span className="badge badge-gray">{typ}</span>;
}

// ─── Erledigen Mini-Modal ─────────────────────────────────────────────────────

function ErledigenMiniModal({ warnung, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const intervall = warnung.intervall_monate || 12;
  const [datum, setDatum] = useState(today);
  const [naechstesDatum, setNaechstesDatum] = useState(() => addMonths(today, intervall));
  const [naechsteKm, setNaechsteKm] = useState('');
  const [notiz, setNotiz] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleDatumChange = (val) => { setDatum(val); setNaechstesDatum(addMonths(val, intervall)); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await api.erledigenFaelligkeit(warnung.fahrzeug_id, {
        typ: warnung.typ, datum,
        naechstes_datum: naechstesDatum || undefined,
        naechste_km: naechsteKm ? parseInt(naechsteKm) : undefined,
        notiz: notiz || undefined
      });
      onDone();
    } catch (err) { setError(err.message); setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{warnung.typ} erledigen – {warnung.kennzeichen}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group form-full">
                <label className="form-label">Erledigungsdatum</label>
                <input className="form-input" type="date" value={datum} onChange={e => handleDatumChange(e.target.value)} required />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Nächstes Fälligkeitsdatum</label>
                <input className="form-input" type="date" value={naechstesDatum} onChange={e => setNaechstesDatum(e.target.value)} />
                <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
                  Automatisch: {datum} + {intervall} Monate
                </div>
              </div>
              {warnung.typ === 'Inspektion' && (
                <div className="form-group form-full">
                  <label className="form-label">Nächste Inspektion bei km (optional)</label>
                  <input className="form-input" type="number" min="0" value={naechsteKm} onChange={e => setNaechsteKm(e.target.value)} placeholder="z.B. 80000" />
                </div>
              )}
              <div className="form-group form-full">
                <label className="form-label">Notiz (optional)</label>
                <input className="form-input" value={notiz} onChange={e => setNotiz(e.target.value)} placeholder="z.B. Prüfstelle, Kosten…" />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Speichern…' : '✓ Erledigen & Termin setzen'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Aufgaben zusammenführen & sortieren ──────────────────────────────────────

function buildAufgaben(warnungen, ereignisseOffen) {
  const fälligkeiten = warnungen.map(w => ({ ...w, _type: 'faelligkeit' }));
  const ereignisse = (ereignisseOffen || []).map(e => ({ ...e, _type: 'ereignis' }));

  // Sortierung: überfällig zuerst, dann offene Ereignisse, dann bald fällig
  // Innerhalb der Gruppen nach Dringlichkeit
  function priority(item) {
    if (item._type === 'faelligkeit') {
      return item.ueberfaellig ? 0 : 2;
    }
    return 1; // Ereignisse dazwischen
  }

  function secondarySort(item) {
    if (item._type === 'faelligkeit') return item.diffDays ?? 0;
    // Ereignisse: ältestes zuerst
    return item.datum ? new Date(item.datum).getTime() : Date.now();
  }

  return [...fälligkeiten, ...ereignisse].sort((a, b) => {
    const pa = priority(a), pb = priority(b);
    if (pa !== pb) return pa - pb;
    const sa = secondarySort(a), sb = secondarySort(b);
    return sa - sb;
  });
}

// ─── Filter Kachel ────────────────────────────────────────────────────────────

function FilterKachel({ label, count, active, color, onClick }) {
  const colors = {
    danger: { bg: active ? 'var(--danger)' : '#fff', text: active ? '#fff' : 'var(--danger)', border: 'var(--danger)', val: active ? '#fff' : 'var(--danger)' },
    warning: { bg: active ? 'var(--warning)' : '#fff', text: active ? '#fff' : 'var(--warning)', border: 'var(--warning)', val: active ? '#fff' : 'var(--warning)' },
    blue: { bg: active ? 'var(--primary)' : '#fff', text: active ? '#fff' : 'var(--primary)', border: 'var(--primary)', val: active ? '#fff' : 'var(--primary)' },
    gray: { bg: active ? 'var(--gray-700)' : '#fff', text: active ? '#fff' : 'var(--gray-700)', border: 'var(--gray-300)', val: active ? '#fff' : 'var(--gray-700)' },
  };
  const c = colors[color] || colors.gray;
  return (
    <div
      onClick={onClick}
      style={{
        background: c.bg,
        border: `2px solid ${c.border}`,
        borderRadius: 'var(--radius-lg)',
        padding: '16px 20px',
        cursor: 'pointer',
        transition: 'all .15s',
        userSelect: 'none',
        boxShadow: active ? '0 2px 8px rgba(0,0,0,.12)' : 'var(--shadow)',
        minWidth: 130,
      }}
    >
      <div style={{ fontSize: 28, fontWeight: 800, color: c.val, lineHeight: 1 }}>{count}</div>
      <div style={{ fontSize: 12, fontWeight: 500, color: c.text, marginTop: 5, opacity: active ? 1 : 0.8 }}>{label}</div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Dashboard({ navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erledigenWarnung, setErledigenWarnung] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null); // null | 'ueberfaellig' | 'bald_faellig' | 'ereignisse' | 'leasing_km'

  const load = () => {
    setLoading(true);
    api.getDashboard().then(setData).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(load, []);

  if (loading) return <div className="spinner">Lade Dashboard…</div>;
  if (!data) return <div className="alert alert-error">Fehler beim Laden.</div>;

  const { stats, warnungen, ereignisse_offen = [] } = data;
  const alleAufgaben = buildAufgaben(warnungen, ereignisse_offen);

  // Filter anwenden
  let gefilterteAufgaben = alleAufgaben;
  if (activeFilter === 'ueberfaellig')  gefilterteAufgaben = alleAufgaben.filter(i => i._type === 'faelligkeit' && i.ueberfaellig);
  if (activeFilter === 'bald_faellig')  gefilterteAufgaben = alleAufgaben.filter(i => i._type === 'faelligkeit' && !i.ueberfaellig);
  if (activeFilter === 'ereignisse')    gefilterteAufgaben = alleAufgaben.filter(i => i._type === 'ereignis');
  if (activeFilter === 'leasing_km')    gefilterteAufgaben = alleAufgaben.filter(i => i.typ === 'Leasing-km');

  const toggleFilter = (f) => setActiveFilter(prev => prev === f ? null : f);

  // Kachel-Zahlen
  const nUeberfaellig  = alleAufgaben.filter(i => i._type === 'faelligkeit' && i.ueberfaellig).length;
  const nBaldFaellig   = alleAufgaben.filter(i => i._type === 'faelligkeit' && !i.ueberfaellig).length;
  const nEreignisse    = ereignisse_offen.length;
  const nLeasingKm     = warnungen.filter(w => w.typ === 'Leasing-km').length;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-subtitle">{alleAufgaben.length} offene Aufgabe{alleAufgaben.length !== 1 ? 'n' : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {warnungen.length > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={() => exportFaelligkeiten(warnungen)}>
              Fälligkeiten PDF
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => {
            api.getAllRadsaetze().then(r => exportRadsaetzeUebersicht(r)).catch(e => alert('Fehler: ' + e.message));
          }}>
            Radsätze PDF
          </button>
          <button className="btn btn-secondary btn-sm" onClick={load}>
            ↻ Aktualisieren
          </button>
        </div>
      </div>

      {/* Filter Kacheln */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 28, flexWrap: 'wrap' }}>
        <FilterKachel
          label="Überfällig"
          count={nUeberfaellig}
          active={activeFilter === 'ueberfaellig'}
          color="danger"
          onClick={() => toggleFilter('ueberfaellig')}
        />
        <FilterKachel
          label="Bald fällig"
          count={nBaldFaellig}
          active={activeFilter === 'bald_faellig'}
          color="warning"
          onClick={() => toggleFilter('bald_faellig')}
        />
        <FilterKachel
          label="Offene Ereignisse"
          count={nEreignisse}
          active={activeFilter === 'ereignisse'}
          color="blue"
          onClick={() => toggleFilter('ereignisse')}
        />
        {nLeasingKm > 0 && (
          <FilterKachel
            label="Leasing km"
            count={nLeasingKm}
            active={activeFilter === 'leasing_km'}
            color="danger"
            onClick={() => toggleFilter('leasing_km')}
          />
        )}
        {activeFilter && (
          <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setActiveFilter(null)}
              style={{ height: 36 }}
            >
              × Filter aufheben
            </button>
          </div>
        )}
      </div>

      {/* Aufgabenliste */}
      {gefilterteAufgaben.length === 0 ? (
        <div className="card">
          <div className="card-body empty-state">
            <div className="empty-state-icon">✅</div>
            <div className="empty-state-title">
              {activeFilter ? 'Keine Einträge für diesen Filter' : 'Alles im grünen Bereich'}
            </div>
            <div className="empty-state-desc">
              {activeFilter ? 'Anderen Filter wählen oder Filter aufheben.' : 'Keine anstehenden Fälligkeiten oder offenen Ereignisse.'}
            </div>
          </div>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Kennzeichen</th>
                <th>Fahrzeug</th>
                <th>Kunde</th>
                <th>Typ</th>
                <th>Fällig / Info</th>
                <th>Status</th>
                <th>Kontakt</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {gefilterteAufgaben.map((item, i) => {
                if (item._type === 'faelligkeit') {
                  return (
                    <tr key={`f-${i}`} className={`warn-row ${item.ueberfaellig ? 'overdue' : 'soon'}`}>
                      <td><strong>{item.kennzeichen}</strong></td>
                      <td>{[item.marke, item.modell].filter(Boolean).join(' ') || '–'}</td>
                      <td>{item.kunden_name}</td>
                      <td><TypBadge typ={item.typ} /></td>
                      <td style={{ fontSize: 13 }}>
                        {item.typ === 'Leasing-km' ? (
                          <span style={{ color: 'var(--gray-600)' }}>
                            {item.leasing_info
                              ? `Hochr. ${item.leasing_info.km_hochgerechnet?.toLocaleString('de-DE')} km (+${item.leasing_info.km_ueberschreitung?.toLocaleString('de-DE')} km)`
                              : formatDate(item.datum)
                            }
                          </span>
                        ) : formatDate(item.datum)}
                      </td>
                      <td>
                        {item.typ === 'Leasing-km'
                          ? <span className="badge badge-danger">km-Limit</span>
                          : <DiffBadge diffDays={item.diffDays} ueberfaellig={item.ueberfaellig} />
                        }
                      </td>
                      <td>
                        {item.kontakt ? (
                          <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                            <div style={{ fontWeight: 600 }}>{item.kontakt.name}</div>
                            {item.kontakt.telefon && (
                              <a href={`tel:${item.kontakt.telefon}`} style={{ color: 'var(--primary)' }}>
                                {item.kontakt.telefon}
                              </a>
                            )}
                          </div>
                        ) : <span style={{ color: 'var(--gray-300)', fontSize: 12 }}>–</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => navigate('fahrzeug-detail', { fahrzeugId: item.fahrzeug_id, kundenId: item.kunden_id })}
                          >
                            Fahrzeug →
                          </button>
                          {['HU', 'SP', 'Inspektion', 'Versicherung'].includes(item.typ) && (
                            <button className="btn btn-secondary btn-sm" onClick={() => setErledigenWarnung(item)}>
                              ✓ Erledigen
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }

                // Ereignis
                return (
                  <tr key={`e-${item.id}`}>
                    <td><strong>{item.kennzeichen}</strong></td>
                    <td>{[item.marke, item.modell].filter(Boolean).join(' ') || '–'}</td>
                    <td>{item.kunden_name}</td>
                    <td><TypBadge typ={item.typ} /></td>
                    <td style={{ fontSize: 13 }}>
                      <div>{formatDate(item.datum)}</div>
                      {item.beschreibung && (
                        <div style={{ color: 'var(--gray-500)', fontSize: 12, marginTop: 2, maxWidth: 260 }}
                          title={item.beschreibung}>
                          {item.beschreibung.length > 60 ? item.beschreibung.slice(0, 60) + '…' : item.beschreibung}
                        </div>
                      )}
                    </td>
                    <td><span className="badge badge-warning">Offen</span></td>
                    <td><span style={{ color: 'var(--gray-300)', fontSize: 12 }}>–</span></td>
                    <td>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate('fahrzeug-detail', { fahrzeugId: item.fahrzeug_id, kundenId: item.kunden_id })}
                      >
                        Fahrzeug →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {erledigenWarnung && (
        <ErledigenMiniModal
          warnung={erledigenWarnung}
          onClose={() => setErledigenWarnung(null)}
          onDone={() => { setErledigenWarnung(null); load(); }}
        />
      )}
    </div>
  );
}
